import React, { useEffect, useState } from 'react';
import { Button, useNotify } from 'react-admin';
import TextField from '@material-ui/core/TextField';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import dataProvider from './../lib/dataProvider';

const dateRange = cave => {
  if (!cave.first_tour && !cave.last_tour) return '-';
  if (cave.first_tour===cave.last_tour) return cave.first_tour;
  return (cave.first_tour || '-')+' – '+(cave.last_tour || '-');
};

const CaveDetails = ({ detail }) => {
  if (!detail) return null;
  if (!detail.tours.length) return <Typography>Keine aktiven Touren vorhanden.</Typography>;
  return (
    <table style={{ width: '100%', fontSize: '0.9em' }}>
      <thead>
        <tr>
          <th>Tour</th><th>Datum / Dauer</th><th>Teilnehmer</th><th>Bemerkung</th><th>NITA</th><th>Gebiete</th><th>Aktivitäten</th><th>Autor</th><th>Dokumente</th>
        </tr>
      </thead>
      <tbody>
        {detail.tours.map(tour =>
          <tr key={tour.id}>
            <td>{tour.id}</td>
            <td>{tour.start_datum}{tour.end_datum ? ' – '+tour.end_datum : ''}{tour.dauer ? ' / '+tour.dauer : ''}</td>
            <td>{tour.user_list || '-'}</td>
            <td>{tour.bemerkung || '-'}</td>
            <td>{tour.nita_id ? tour.nita_id+(tour.nita_section ? ' / '+tour.nita_section : '') : '-'}</td>
            <td>{tour.cavepart_list || '-'}</td>
            <td>{tour.activity_list || '-'}</td>
            <td>{tour.owner_name || '-'}</td>
            <td>{tour.anhang_anz ? tour.anhang_anz+(tour.anhang_list ? ': '+tour.anhang_list : '') : '-'}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const CaveMerge = () => {
  const notify = useNotify();
  const [caves, setCaves] = useState([]);
  const [regions, setRegions] = useState([]);
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('');
  const [includeZzz, setIncludeZzz] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState({});
  const [sources, setSources] = useState([]);
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const listParams = filter => ({
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'cave_name', order: 'ASC' },
    filter: filter,
  });

  const loadCaves = () => {
    dataProvider.getList('caveMerge', listParams({q:query, id_region:region, include_zzz:includeZzz}))
      .then(result => setCaves(result.data))
      .catch(error => notify(error.message || 'Höhlen konnten nicht geladen werden.', 'warning'));
  };

  useEffect(() => {
    loadCaves();
  }, [query, region, includeZzz]);

  useEffect(() => {
    dataProvider.getList('region', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'region_name', order: 'ASC' },
      filter: {},
    })
      .then(result => setRegions(result.data))
      .catch(() => {});
  }, []);

  const invalidatePreview = () => setPreview(false);

  const toggleDetail = id => {
    if (expanded===id) {
      setExpanded(false);
      return;
    }
    setExpanded(id);
    if (!details[id]) {
      dataProvider.getOne('caveMerge/detail', {id:id})
        .then(result => setDetails({...details, [id]:result.data}))
        .catch(error => notify(error.message || 'Details konnten nicht geladen werden.', 'warning'));
    }
  };

  // ids als string vergleichen: je nach abfrage (z.b. nach dem filtern) kommen sie als zahl oder als string
  const inSources = id => sources.some(source => String(source.id)===String(id));

  const addSource = cave => {
    if (inSources(cave.id)) return;
    setSources([...sources, cave]);
    invalidatePreview();
  };

  const removeSource = id => {
    setSources(sources.filter(source => String(source.id)!==String(id)));
    if (String(target)===String(id)) setTarget('');
    invalidatePreview();
  };

  const getPayload = () => {
    if (!target) return false;
    const sourceIds = sources.filter(source => String(source.id)!==String(target)).map(source => source.id);
    if (!sourceIds.length) return false;
    return {target_cave_id:target, source_cave_ids:sourceIds};
  };

  const previewMerge = () => {
    const payload = getPayload();
    if (!payload) return;
    setLoading(true);
    dataProvider.create('caveMerge/preview', {
      data: payload,
    })
      .then(result => {
        if (!result.data.target_cave || !Array.isArray(result.data.source_caves)) {
          throw new Error('Ungültige Antwort für die Merge-Vorschau.');
        }
        setPreview(result.data);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        notify(error.message || 'Vorschau konnte nicht erstellt werden.', 'warning');
      });
  };

  const executeMerge = () => {
    if (!preview || !window.confirm('Die Zusammenführung jetzt endgültig ausführen?')) return;
    const payload = getPayload();
    if (!payload) return;
    setLoading(true);
    dataProvider.create('caveMerge/execute', {
      data: payload,
    })
      .then(result => {
        setLoading(false);
        notify(result.data.tour_count+' Touren und '+result.data.cavepart_count+' Höhlengebiete wurden umgehängt.', 'info');
        setSources([]);
        setTarget('');
        setPreview(false);
        setDetails({});
        loadCaves();
      })
      .catch(error => {
        setLoading(false);
        notify(error.message || 'Zusammenführung konnte nicht ausgeführt werden.', 'warning');
      });
  };

  return (
    <div style={{ padding: '1em', maxWidth: 1600 }}>
      <h2>Höhlen zusammenführen</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', flexWrap: 'wrap' }}>
        <TextField label="Höhle suchen" value={query} onChange={event => setQuery(event.target.value)} />
        <TextField select label="Region" value={region} onChange={event => setRegion(event.target.value)} style={{ minWidth: 240 }}>
          <MenuItem value="">Alle Regionen</MenuItem>
          {regions.map(item => <MenuItem key={item.id} value={item.id}>{item.countryregion_name || item.region_name}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={<Checkbox checked={includeZzz} onChange={event => setIncludeZzz(event.target.checked)} />}
          label="Bereits mit zzz markierte Höhlen anzeigen"
        />
      </div>

      <div style={{ marginTop: '1em' }}>
        {caves.map(cave =>
          <ExpansionPanel key={cave.id} expanded={expanded===cave.id} onChange={() => toggleDetail(cave.id)}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '1em', flexWrap: 'wrap' }}>
                <strong>{cave.cave_name}</strong>
                <span>{cave.kanton_land || '-'}</span>
                <span>Touren: {cave.tour_anz}</span>
                <span>{dateRange(cave)}</span>
                <span style={{ marginLeft: 'auto' }}>
                  {/* stopPropagation: klick auf den button soll das akkordeon nicht auf-/zuklappen */}
                  <Button label="Zum Merge hinzufügen" variant="outlined" size="small"
                    onClick={event => { event.stopPropagation(); addSource(cave); }}
                    onFocus={event => event.stopPropagation()}
                    disabled={inSources(cave.id)}>
                    <AddIcon />
                  </Button>
                </span>
              </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails style={{ display: 'block' }}>
              <CaveDetails detail={details[cave.id]} />
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}
      </div>

      <div style={{ marginTop: '2em' }}>
        <h3>Merge-Warenkorb</h3>
        {!sources.length ? <Typography>Keine Höhlen ausgewählt.</Typography> : null}
        {sources.map(source =>
          <div key={source.id} style={{ marginBottom: '0.4em' }}>
            <label style={{ marginRight: '1em' }}>
              <input type="radio" checked={String(target)===String(source.id)} disabled={source.cave_name.indexOf('zzz ')===0} onChange={() => { setTarget(source.id); invalidatePreview(); }} />
              {' '}Behalten
            </label>
            <strong>{source.cave_name}</strong> (ID {source.id}) – {source.kanton_land || '-'} – Touren: {source.tour_anz} – {dateRange(source)}
            <Button label="Entfernen" variant="outlined" size="small" style={{ marginLeft: '1em' }} onClick={() => removeSource(source.id)}><RemoveIcon /></Button>
          </div>
        )}
        <div style={{ marginTop: '1em' }}>
          <Button label="Vorschau erstellen" variant="contained" onClick={previewMerge} disabled={!getPayload() || loading}>
            <VisibilityIcon />
          </Button>
        </div>
      </div>

      {preview ?
        <div style={{ marginTop: '2em' }}>
          <h3>Vorschau</h3>
          <Typography>Zielhöhle: <strong>{preview.target_cave.cave_name}</strong></Typography>
          {preview.source_caves.map(source =>
            <div key={source.id} style={{ marginTop: '1em' }}>
              <strong>{source.cave_name}</strong> wird zu <strong>{source.planned_cave_name}</strong>.
              <div>{source.tours.length} Touren, {source.caveparts.length} Höhlengebiete.</div>
              {source.tours.map(tour =>
                <div key={tour.id} style={{ marginLeft: '1em' }}>
                  Tour {tour.id}: {tour.planned_note}
                </div>
              )}
            </div>
          )}
          <div style={{ marginTop: '1em' }}>
            <Button label="Zusammenführung ausführen" variant="contained" onClick={executeMerge} disabled={loading}>
              <MergeTypeIcon />
            </Button>
          </div>
        </div>
      : null}
    </div>
  );
};

export default {
  options: { label: 'Höhlen zusammenführen' },
  list: (props) => <CaveMerge {...props} />,
};
